import type { Project } from './types';
import { hideProjectTooltip } from './stores/projectTooltip';
import { draggedProject } from './stores/ui';
import { get } from 'svelte/store';

/**
 * Callback invoked when a project is dropped onto a target.
 *
 * @param projectId - The ID of the dropped project (from dataTransfer)
 * @param project - The full Project object being dragged
 * @param dropKey - The drop target key (period key for Rolling views, status key for Focus views)
 */
export type DropHandler = (projectId: string, project: Project, dropKey: string) => void;

/**
 * Creates drag-and-drop state management for project chips.
 *
 * Manages dragging project ID, drop target highlighting, and drag event handlers.
 * The consumer provides the onDrop callback to handle the actual move logic
 * (calling requestProjectMove with the appropriate move type and params).
 *
 * @param onDrop - Called when a project is dropped. Consumer is responsible for
 *   calling requestProjectMove() with the correct move type and target params.
 */
export function useProjectDragDrop(onDrop: DropHandler) {
  let draggingProjectId = $state<string | null>(null);
  let dropTarget = $state<string | null>(null);
  let sourceKey = $state<string | null>(null);

  function handleDragStart(e: DragEvent, project: Project, source?: string) {
    hideProjectTooltip(true);
    draggingProjectId = project.id;
    sourceKey = source ?? null;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(project.id));
    }
  }

  function handleDragEnd() {
    draggingProjectId = null;
    dropTarget = null;
    sourceKey = null;
    draggedProject.set(null);
  }

  function handleDragOver(e: DragEvent, key: string) {
    e.preventDefault();
    if (get(draggedProject)) {
      dropTarget = key;
    }
  }

  function handleDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      dropTarget = null;
    }
  }

  function handleDrop(e: DragEvent, key: string) {
    e.preventDefault();
    dropTarget = null;

    const currentDragged = get(draggedProject);
    if (!currentDragged) return;

    if (sourceKey && sourceKey === key) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId) {
      onDrop(projectId, currentDragged, key);
    }
  }

  return {
    get draggingProjectId() {
      return draggingProjectId;
    },
    get dropTarget() {
      return dropTarget;
    },
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
